import type { ComponentType } from "react";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDatePicker,
  RHFRadioGroup,
  RHFSelect,
  RHFSwitch,
  RHFTextField,
} from "src/components/hook-form";
import { manualDocumentCountryOptions } from "src/pages/ManualDocumentV2/utils/country";
import { currencySymbols } from "src/utils/currency";
import type { IFieldConfig, TFieldType } from "../types";
import CustomerLookupField from "./fields/CustomerLookupField";
import RadioGroupField from "./fields/RadioGroupField";
import VendorLookupField from "./fields/VendorLookupField";

type TRenderer = (cfg: IFieldConfig) => JSX.Element;

const inputRenderer: TRenderer = (cfg) => (
  <RHFTextField
    name={cfg.name}
    label={cfg.label}
    type={cfg.inputType ?? "text"}
    {...cfg.props}
  />
);

const dateRenderer: TRenderer = (cfg) => (
  <RHFDatePicker name={cfg.name} label={cfg.label} {...cfg.props} />
);

const autocompleteRenderer: TRenderer = (cfg) => (
  <RHFAutocomplete
    name={cfg.name}
    label={cfg.label}
    options={cfg.options ?? cfg.props?.options ?? []}
    {...cfg.props}
  />
);

const selectRenderer: TRenderer = (cfg) => (
  <RHFSelect name={cfg.name} label={cfg.label} {...cfg.props}>
    {(cfg.options ?? []).map((opt) => (
      <option key={String(opt.value)} value={String(opt.value)}>
        {opt.label}
      </option>
    ))}
  </RHFSelect>
);

const checkboxRenderer: TRenderer = (cfg) => (
  <RHFCheckbox name={cfg.name} label={cfg.label} {...cfg.props} />
);

const switchRenderer: TRenderer = (cfg) => (
  <RHFSwitch name={cfg.name} label={cfg.label} {...cfg.props} />
);

const radioRenderer: TRenderer = (cfg) => (
  <RadioGroupField config={cfg} />
);

const currencyRenderer: TRenderer = (cfg) => (
  <RHFAutocomplete
    name={cfg.name}
    label={cfg.label}
    options={Object.entries(currencySymbols).map(([key, symbol]) => ({
      value: key,
      label: `${key} ${symbol}`,
    }))}
    {...cfg.props}
  />
);

const countryRenderer: TRenderer = (cfg) => (
  <RHFAutocomplete
    name={cfg.name}
    label={cfg.label}
    options={manualDocumentCountryOptions}
    {...cfg.props}
  />
);

const customerLookupRenderer: TRenderer = (cfg) => <CustomerLookupField config={cfg} />;
const vendorLookupRenderer: TRenderer = (cfg) => <VendorLookupField config={cfg} />;

export const fieldRegistry: Record<Exclude<TFieldType, "custom">, TRenderer> = {
  input: inputRenderer,
  date: dateRenderer,
  autocomplete: autocompleteRenderer,
  select: selectRenderer,
  checkbox: checkboxRenderer,
  switch: switchRenderer,
  radio: radioRenderer,
  currency: currencyRenderer,
  country: countryRenderer,
  customerLookup: customerLookupRenderer,
  vendorLookup: vendorLookupRenderer,
};

export const customFieldRegistry = new Map<string, ComponentType<{ config: IFieldConfig }>>();

export const registerCustomField = (
  key: string,
  Component: ComponentType<{ config: IFieldConfig }>,
) => {
  customFieldRegistry.set(key, Component);
};
